import { useEffect, useRef, useState } from 'react';

import {
  CloseCircleFilled,
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button as ButtonV2, Colors, Flex, Text } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { Toast, CloseX } from '@revenuemarket/ui';
import { validateEmail } from '@revenuemarket/utils';
import { Button, Checkbox, Divider, Form, Input, InputRef, Select, Tooltip } from 'antd';
import { FormInstance } from 'antd/lib/form/Form';
import * as yup from 'yup';

import { IMembersItem } from '~apis/contract';
import { CompanyUserPermission } from '~apis/role/type';
import { ModifyReason } from '~apis/tax-invoice';
import { usePermissionCheck } from '~components/Role/PermissionGuard';
import BusinessEntityLabelSelect from '~components/RuleManagement/RuleManageItem/LabelSelect/BusinessEntityLabelSelect';
import { AddressModal } from '~components/ui/Modal/AddressModal';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useBusinessEntityDetails, useUpdateBusinessEntity } from '~hooks/queries/contract/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { formatAddress, parseAddress } from '~utils/common/address';

import { Section } from './Section';
import { IssuanceFormData, IssuanceUpdateFormData } from '../type';

const Scheme = yup.object({
  email: COMMON_YUP.email,
});

const ReferenceEmail: React.FC<{
  form: FormInstance<IssuanceFormData | IssuanceUpdateFormData>;
}> = ({ form }) => {
  const values = Form.useWatch([], form);

  const [emailReferenceStatus, setEmailReferenceStatus] = useState<'error' | undefined>();
  const [referenceEmail, setReferenceEmail] = useState('');
  return (
    <Flex direction="column">
      <Form.Item
        style={{
          width: '100%',
        }}
        name={['emailResends']}
        hidden
      />
      <Form.Item
        style={{
          width: '100%',
          marginBottom: 0,
        }}
        label={
          <Flex align="center" gap={5}>
            <Text
              style={{
                color: Colors.gray600,
              }}
            >
              이메일 추가 발송
            </Text>
          </Flex>
        }
        help={emailReferenceStatus === 'error' ? '이메일 형식이 올바르지 않습니다.' : undefined}
        validateStatus={emailReferenceStatus}
      >
        <Flex align="center" gap={8}>
          <Input
            placeholder="이메일을 입력하세요. 세금계산서에는 기재되지 않아요."
            suffix={
              <CloseCircleFilled
                style={{
                  color: Colors.red400,
                  visibility: emailReferenceStatus === 'error' ? 'visible' : 'hidden',
                }}
              />
            }
            status={emailReferenceStatus}
            value={referenceEmail}
            onChange={(e) => {
              if (!validateEmail(e.target.value)) {
                setEmailReferenceStatus('error');
              } else {
                setEmailReferenceStatus(undefined);
              }

              setReferenceEmail(e.target.value);
            }}
          />
          <ButtonV2
            border
            type="button"
            disable={emailReferenceStatus === 'error' || !referenceEmail}
            onClick={() => {
              setReferenceEmail('');
              form.setFieldsValue({
                emailResends: [...(values?.emailResends ?? []), referenceEmail],
              });
            }}
            style={{
              width: 50,
              height: 28,
            }}
          >
            추가
          </ButtonV2>
        </Flex>
      </Form.Item>
      <Flex
        direction="column"
        style={{
          paddingLeft: 7,
        }}
      >
        {(values?.emailResends ?? [])?.map((email, index) => (
          <Flex key={index} align="center" justify="space-between">
            <Text>{email}</Text>
            <ButtonV2
              buttonType="transparent"
              icon={<CloseX width={16} height={16} />}
              type="button"
              onClick={() => {
                form.setFieldsValue({
                  emailResends: values?.emailResends?.filter((item) => item !== email),
                });
              }}
            ></ButtonV2>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

export const ManualAdd: React.FC<{
  form: FormInstance<IssuanceFormData | IssuanceUpdateFormData>;
  disableFields?: string[];
  disabled?: boolean;
  showReferenceEmail?: boolean;
}> = ({ form, disableFields, disabled, showReferenceEmail = true }) => {
  const detailAddressInputRef = useRef<InputRef>(null);
  return (
    <>
      <Form.Item
        required
        name={['contractorManualInfo', 'corpName']}
        label={
          <Flex align="center" gap={5}>
            <Text
              style={{
                color: Colors.gray600,
              }}
            >
              상호(법인명)
            </Text>
          </Flex>
        }
        style={{
          flex: 1,
          width: '100%',
        }}
      >
        <Input disabled={disableFields?.includes('name') || disabled} />
      </Form.Item>
      <Flex align="flex-start" gap={16}>
        <Form.Item
          style={{
            flex: 1,
            width: '100%',
          }}
          required
          name={['contractorManualInfo', 'businessRegNo']}
          label={
            <Flex align="center" gap={5}>
              <Text
                style={{
                  color: Colors.gray600,
                }}
              >
                등록번호
              </Text>
            </Flex>
          }
        >
          <Input disabled={disableFields?.includes('registrationNumber') || disabled} />
        </Form.Item>
        <Form.List name={['contractorManualInfo', 'corpCeo']}>
          {(fields, { add, remove }) => {
            return (
              <Flex
                direction="column"
                gap={4}
                style={{
                  flex: 1,
                  width: '100%',
                }}
              >
                <Flex gap={4} align="center">
                  <Text style={{ color: Colors.red300 }}>*</Text>
                  <Text style={{ color: Colors.gray600 }}>성명</Text>
                </Flex>
                <div>
                  <Flex gap={4} align="flex-end">
                    <Flex gap={4} align="center" direction="column">
                      {fields.map((field, index) => {
                        return (
                          <Form.Item
                            {...field}
                            key={field.key}
                            style={{
                              margin: 0,
                            }}
                          >
                            <Input
                              style={{
                                width: 200,
                              }}
                              disabled={disabled}
                              suffix={
                                index === 0 ? (
                                  <div />
                                ) : (
                                  <MinusCircleOutlined
                                    style={{
                                      color: Colors.gray500,
                                    }}
                                    onClick={() => {
                                      remove(index);
                                    }}
                                  />
                                )
                              }
                            />
                          </Form.Item>
                        );
                      })}
                    </Flex>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        add();
                      }}
                      disabled={disabled}
                    >
                      대표자 추가
                    </Button>
                  </Flex>
                </div>
              </Flex>
            );
          }}
        </Form.List>
      </Flex>
      <Flex direction="column" style={{ marginBottom: 16 }}>
        <Flex gap={4} align="center">
          <Text
            style={{
              color: disabled ? Colors.gray600 : Colors.red500,
            }}
          >
            *
          </Text>
          <Text style={{ color: Colors.gray600 }}>주소</Text>
        </Flex>
        <Form.Item
          name={['contractorManualInfo', 'corpAddress']}
          required
          style={{
            marginBottom: 4,
          }}
        >
          <Input
            disabled={disabled}
            readOnly
            placeholder="기본 주소"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              overlays.open(({ isOpen, onClose }) => (
                <AddressModal
                  isOpen={isOpen}
                  onClose={onClose}
                  onComplete={(address) => {
                    form.setFieldsValue({
                      contractorManualInfo: {
                        corpAddress: formatAddress(parseAddress(address)),
                        corpAddressDetail: '',
                      },
                    });

                    onClose();
                    // WARNING: antd delays recover focus to form item input
                    setTimeout(() => {
                      detailAddressInputRef.current?.focus();
                    }, 500);
                  }}
                />
              ));
            }}
          />
        </Form.Item>
        <Form.Item name={['contractorManualInfo', 'corpAddressDetail']} noStyle>
          <Input disabled={disabled} ref={detailAddressInputRef} placeholder="상세 주소" />
        </Form.Item>
      </Flex>
      <Flex gap={16} align="center">
        <Form.Item
          name={['contractorManualInfo', 'taxIdNumber']}
          rules={[
            {
              validator: async (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (value.length !== 4) {
                  return Promise.reject('종사업장 4자리를 입력해주세요.');
                }
                return Promise.resolve();
              },
            },
          ]}
          label={
            <Flex align="center" gap={5}>
              <Text
                style={{
                  color: Colors.gray600,
                }}
              >
                종사업장
              </Text>
            </Flex>
          }
          style={{
            flex: 1,
            width: '100%',
          }}
        >
          <Input placeholder="종사업장 번호를 입력하세요" disabled={disabled} />
        </Form.Item>
        <Form.Item
          name={['contractorManualInfo', 'businessType']}
          label={
            <Flex align="center" gap={5}>
              <Text
                style={{
                  color: Colors.gray600,
                }}
              >
                업태
              </Text>
            </Flex>
          }
          style={{
            flex: 1,
            width: '100%',
          }}
        >
          <Input placeholder="업태를 입력하세요" disabled={disabled} />
        </Form.Item>
        <Form.Item
          name={['contractorManualInfo', 'businessClass']}
          label={
            <Flex align="center" gap={5}>
              <Text
                style={{
                  color: Colors.gray600,
                }}
              >
                업종
              </Text>
            </Flex>
          }
          style={{
            flex: 1,
            width: '100%',
          }}
        >
          <Input placeholder="업종을 입력하세요" disabled={disabled} />
        </Form.Item>
      </Flex>
      <Flex
        style={{
          marginBottom: 16,
        }}
      >
        <Form.List name={['contractorManualInfo', 'members']}>
          {(fields, { add: _add, remove: _remove }) => {
            return (
              <Flex
                direction="column"
                gap={4}
                style={{
                  flex: 1,
                  width: '100%',
                }}
              >
                <Flex gap={4} align="center">
                  <Text
                    style={{
                      color: disabled ? Colors.gray600 : Colors.red500,
                    }}
                  >
                    *
                  </Text>
                  <Text style={{ color: Colors.gray600 }}>세금계산서 담당자</Text>
                </Flex>

                <div style={{ width: '100%' }}>
                  <Flex gap={4} align="flex-end" style={{ width: '100%' }}>
                    <Flex gap={4} align="center" direction="column" style={{ width: '100%' }}>
                      {fields.map((field) => {
                        return (
                          <Flex key={field.key} gap={4} style={{ width: '100%' }}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'name']}
                              style={{
                                width: 200,
                                margin: 0,
                              }}
                            >
                              <Input placeholder="이름" disabled={disabled} required />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              name={[field.name, 'email']}
                              style={{
                                width: '100%',
                                margin: 0,
                              }}
                              rules={[
                                {
                                  validator: async (_, value) => {
                                    if (!value) {
                                      return Promise.resolve();
                                    }

                                    if (!Scheme.isValidSync({ email: value })) {
                                      return Promise.reject('이메일 형식이 올바르지 않습니다.');
                                    }

                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Input
                                disabled={disabled}
                                placeholder="이메일을 입력하세요 (선택)"
                                width="100%"
                              />
                            </Form.Item>
                          </Flex>
                        );
                      })}
                    </Flex>
                  </Flex>
                </div>
              </Flex>
            );
          }}
        </Form.List>
      </Flex>
      {showReferenceEmail && <ReferenceEmail form={form} />}
    </>
  );
};

const Default: React.FC<{
  editType?: ModifyReason;
  form: FormInstance<IssuanceFormData | IssuanceUpdateFormData>;
  disabled?: boolean;
}> = ({ editType, form, disabled }) => {
  const { companyId } = useCompanyId();
  const { hasPermission: canManageBusinessEntities } = usePermissionCheck(
    CompanyUserPermission.CAN_MANAGE_BUSINESS_ENTITIES,
  );

  const { mutateAsync: updateBusinessEntity } = useUpdateBusinessEntity();
  const [taxInvoiceManagerOptions, setTaxInvoiceManagerOptions] = useState<IMembersItem[]>([]); // 세금계산서 담당자 목록
  const [managerName, setManagerName] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const values = Form.useWatch([], form);
  const targetId = values?.businessEntityInfo?.businessEntityId;
  const { data: businessEntity } = useBusinessEntityDetails({ companyId, id: targetId as number });

  const contractorDetail = businessEntity?.Data?.data;

  const isValidated = false;

  const onAdd = async () => {
    if (!contractorDetail) return;
    const response = await updateBusinessEntity({
      companyId,
      request: {
        bankAccounts: contractorDetail.bankAccounts,
        id: targetId as number,
        name: contractorDetail.name,
        businessRegNo: contractorDetail.businessRegNumber,
        representatives: contractorDetail.members
          .filter((m) => m.type === 'REPRESENTATIVE')
          .map((m) => {
            return {
              id: m.id,
              name: m.name,
            };
          }),
        address: contractorDetail.address,
        btpNo: contractorDetail.btpNo,
        bzdNo: contractorDetail.bzdNo,
        taxRegistrationNo: contractorDetail.taxRegistrationNo,
        taxInvoiceManagers: [
          ...contractorDetail.taxInvoiceManagers,
          {
            name: managerName,
            email: managerEmail,
          },
        ],
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      setManagerEmail('');
      setManagerName('');
      Toast.success('담당자가 추가되었습니다.');
    }
  };

  useEffect(() => {
    if (contractorDetail) {
      setTaxInvoiceManagerOptions(contractorDetail.taxInvoiceManagers);

      form.setFieldsValue({
        contractorInfo: {
          ...values.contractorInfo,
          businessRegNo: contractorDetail.businessRegNumber,
          corpName: contractorDetail.name || contractorDetail.alias,
          corpCeo: contractorDetail.members
            .filter((m) => m.type === 'REPRESENTATIVE')
            .map((m) => m.name),
          taxIdNumber: contractorDetail.taxRegistrationNo,
          corpAddress: contractorDetail.address,
          businessClass: contractorDetail.bzdNo,
          businessType: contractorDetail.btpNo,
          members:
            contractorDetail.taxInvoiceManagers.length > 0
              ? [
                  {
                    name: contractorDetail.taxInvoiceManagers[0].name,
                    email: contractorDetail.taxInvoiceManagers[0].email,
                  },
                ]
              : [],
        },
        businessEntityInfo: {
          businessMemberIdList:
            contractorDetail.taxInvoiceManagers?.length > 0
              ? [contractorDetail.taxInvoiceManagers[0].id]
              : [],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form과 values를 deps에 추가하면 무한루프 발생
  }, [contractorDetail]);

  const taxInvoiceManagerOptionItems = taxInvoiceManagerOptions.map((item) => {
    return {
      label: `${item.name} (${item.email})`,
      value: item.id,
    };
  }) as { label: string; value: number }[];

  return (
    <Flex style={{ width: '100%' }} gap={16}>
      <Form.Item
        required
        label={
          <Flex align="center" gap={5}>
            <Text
              style={{
                color: Colors.gray600,
              }}
            >
              거래처
            </Text>

            <Tooltip title="공급받는자에 입력할 거래처를 선택해주세요. 거래처로 선택하면 나머지 정보를 알아서 채워드려요.">
              <QuestionCircleOutlined
                width={14}
                height={14}
                style={{
                  color: Colors.gray500,
                }}
              />
            </Tooltip>
          </Flex>
        }
        style={{
          flex: 1,
          width: '100%',
        }}
      >
        <Flex direction="column">
          <Form.Item name={['businessEntityInfo', 'businessEntityId']}>
            <BusinessEntityLabelSelect
              type="name"
              width="100%"
              disabled={(editType && editType !== 'CORRECTING_MISSTATEMENTS') || disabled}
              selectedBusinessEntity={values?.businessEntityInfo?.businessEntityId ?? null}
              setSelectedBusinessEntity={async (result) => {
                form.setFieldsValue({
                  businessEntityInfo: {
                    businessEntityId: result as number,
                    businessMemberIdList: [],
                  },
                });
              }}
              hideFooter
            />
          </Form.Item>
          {isValidated && (
            <Text style={{ color: Colors.gray600 }}>
              최근 1달 이내 발급된 세금계산서 또는 발급 예약이 있는 거래처입니다.
            </Text>
          )}
        </Flex>
      </Form.Item>
      <Flex
        style={{
          flex: 1,
          width: '100%',
        }}
        direction="column"
      >
        <Form.Item
          style={{
            width: '100%',
          }}
          name={['businessEntityInfo', 'businessMemberIdList']}
          label={
            <Flex align="center" gap={5}>
              <Text
                style={{
                  color: Colors.gray600,
                }}
              >
                거래처 담당자 (최대 1명)
              </Text>
              <Tooltip title="세금계산서가 발급되면 메일을 수신할 거래처 담당자를 선택해주세요. 담당자 정보가 없다면 빈칸을 클릭하여 새로 만들어주세요.">
                <QuestionCircleOutlined
                  width={14}
                  height={14}
                  style={{
                    color: Colors.gray500,
                  }}
                />
              </Tooltip>
            </Flex>
          }
          required
        >
          <Select
            value={values?.businessEntityInfo?.businessMemberIdList?.[0]}
            onChange={(id) => {
              form.setFieldsValue({
                businessEntityInfo: {
                  businessMemberIdList: [id],
                },
                contractorInfo: {
                  members: [
                    {
                      name: taxInvoiceManagerOptions.find((m) => m.id === id)?.name ?? '',
                      email: taxInvoiceManagerOptions.find((m) => m.id === id)?.email ?? '',
                    },
                  ],
                },
              });
            }}
            placeholder="세금계산서를 수신할 거래처 담당자를 선택하세요"
            options={taxInvoiceManagerOptionItems}
            disabled={(editType && editType !== 'CORRECTING_MISSTATEMENTS') || disabled}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '16px 0' }} />
                {canManageBusinessEntities ? (
                  <Flex direction="column">
                    <Flex
                      gap={8}
                      style={{
                        padding: '0px 12px',
                      }}
                    >
                      <Input
                        style={{
                          width: 100,
                        }}
                        value={managerName}
                        onChange={(e) => {
                          setManagerName(e.target.value);
                        }}
                        placeholder="이름"
                      />
                      <Input
                        value={managerEmail}
                        onChange={(e) => {
                          setManagerEmail(e.target.value);
                        }}
                        placeholder="이메일"
                      />
                    </Flex>
                    <Button
                      icon={<PlusOutlined />}
                      type="ghost"
                      onClick={onAdd}
                      style={{
                        margin: '8px 12px 4px 12px',
                        cursor: !(managerName && managerEmail) ? 'not-allowed' : 'pointer',
                      }}
                      disabled={!(managerName && managerEmail)}
                    >
                      이 거래처에 담당자 추가
                    </Button>
                  </Flex>
                ) : (
                  <div
                    style={{
                      padding: '8px 12px 12px 12px',
                      color: Colors.gray600,
                    }}
                  >
                    <Text style={{ color: 'inherit' }}>담당자를 추가할 권한이 없어요.</Text>
                    <br />
                    <Text style={{ color: 'inherit' }}>관리자에게 아래 권한을 문의해주세요.</Text>
                    <ul style={{ paddingLeft: 18, margin: 0, listStyle: 'disc' }}>
                      <li>
                        <Text style={{ color: 'inherit' }}>거래처 관리</Text>
                      </li>
                    </ul>
                  </div>
                )}
              </>
            )}
          />
        </Form.Item>
        <ReferenceEmail form={form} />
      </Flex>
    </Flex>
  );
};

const ClientInfo: React.FC<{
  form: FormInstance<IssuanceFormData | IssuanceUpdateFormData>;
  editType?: ModifyReason;
}> = ({ form, editType }) => {
  const values = Form.useWatch([], form);
  const isManual = values?.isManualInput;

  return (
    <Section
      direction="column"
      gap={24}
      style={{
        padding: '36px 80px 24px 80px',
      }}
    >
      {' '}
      <Flex justify="space-between">
        <Text textStyle="14_400" style={{ color: Colors.gray600 }}>
          고객 정보
        </Text>
        <Form.Item name="isManualInput" valuePropName="checked">
          <Checkbox disabled={editType && editType !== 'CORRECTING_MISSTATEMENTS'}>
            <Flex align="center" gap={4}>
              <Text>직접 입력</Text>
              <Tooltip title="거래처로 검색하기 어려운 경우 직접 입력할 수 있어요. 공급받는자 정보를 직접 입력하더라도, 등록번호가 같다면 발급 완료된 세금계산서는 기존 거래처로 분류됩니다.">
                <QuestionCircleOutlined
                  width={16}
                  height={16}
                  style={{
                    color: Colors.gray500,
                  }}
                />
              </Tooltip>
            </Flex>
          </Checkbox>
        </Form.Item>
      </Flex>
      <Flex direction="column">
        {isManual ? (
          <ManualAdd form={form} disabled={editType && editType !== 'CORRECTING_MISSTATEMENTS'} />
        ) : (
          <Default
            editType={editType}
            form={form}
            disabled={editType && editType !== 'CORRECTING_MISSTATEMENTS'}
          />
        )}
      </Flex>
    </Section>
  );
};

export default ClientInfo;
